import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// iOS detection
const isIOS = typeof navigator !== 'undefined' && 
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || 
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

function getIsPortrait() {
  if (typeof window === "undefined") return true;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return window.innerHeight >= window.innerWidth;
}

/**
 * SecurityHeaders component
 * Provides client-side security measures including:
 * - Clickjacking protection (frame-busting)
 * - Console log removal in production
 * - iOS Safari zoom prevention on /products page
 * - iOS portrait animation freeze class toggle (useLayoutEffect for pre-paint)
 * - Targeted transform stripping for Framer Motion elements with scroll burst
 */
export function SecurityHeaders() {
  const location = useLocation();
  const stripLoopRef = useRef<number | null>(null);
  const scrollBurstRef = useRef<number | null>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    // Prevent clickjacking - ensure page is not embedded in iframe
    if (window.self !== window.top) {
      try {
        window.top?.location.replace(window.self.location.href);
      } catch {
        // If we can't access top (cross-origin), hide the page
        document.body.style.display = 'none';
      }
    }

    // Remove console logs in production (except errors and warnings)
    if (import.meta.env.PROD) {
      const noop = () => {};
      console.log = noop;
      console.debug = noop;
      console.info = noop;
      // Keep console.error and console.warn for debugging production issues
    }
  }, []);

  // iOS Portrait Class Toggle - useLayoutEffect to apply BEFORE first paint
  useLayoutEffect(() => {
    if (!isIOS) return;
    
    const isProductsPage = location.pathname === '/products';
    
    const updateClass = () => {
      const isPortrait = getIsPortrait();
      if (isProductsPage && isPortrait) {
        document.documentElement.classList.add('ios-products-portrait');
      } else {
        document.documentElement.classList.remove('ios-products-portrait');
      }
    };

    // Apply immediately before paint
    updateClass();

    window.addEventListener('resize', updateClass);
    window.addEventListener('orientationchange', updateClass);

    return () => {
      document.documentElement.classList.remove('ios-products-portrait');
      window.removeEventListener('resize', updateClass);
      window.removeEventListener('orientationchange', updateClass);
    };
  }, [location.pathname]);

  // Targeted transform stripping for Framer Motion elements on iOS /products in portrait
  // Using useLayoutEffect to start stripping before paint
  useLayoutEffect(() => {
    const isProductsListPage = location.pathname === '/products';
    if (!isProductsListPage || !isIOS) return;

    // Debug mode - enable with ?iosZoomDebug=1
    const debugMode = new URLSearchParams(window.location.search).has('iosZoomDebug');
    const log = debugMode ? console.warn.bind(console, '[iOS Transform Strip]') : () => {};

    const isPortrait = getIsPortrait();
    if (!isPortrait) {
      log('Landscape mode, skipping transform strip');
      return;
    }

    log('Starting targeted transform strip loop (useLayoutEffect)');
    hasScrolledRef.current = false;

    const startTime = Date.now();
    const duration = 3000; // Extended to 3 seconds for initial window

    const stripTransforms = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed > duration) {
        log('Transform strip loop complete');
        stripLoopRef.current = null;
        return;
      }

      // Use broad selector for first 500ms to catch everything during initial mount
      // Then narrow down to targeted selectors for the remainder
      const useNarrowSelector = elapsed > 500;
      const selector = useNarrowSelector
        ? '[data-framer-component-type], [data-projection-id], [style*="transform"]'
        : '*';
      
      const nodes = document.querySelectorAll(selector);

      let strippedCount = 0;
      nodes.forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.style.transform || (htmlEl.style as any).webkitTransform) {
          htmlEl.style.transform = '';
          (htmlEl.style as any).webkitTransform = '';
          strippedCount++;
        }
      });

      if (debugMode && strippedCount > 0) {
        log(`Stripped transforms from ${strippedCount} elements (${useNarrowSelector ? 'narrow' : 'broad'} selector)`);
      }

      stripLoopRef.current = requestAnimationFrame(stripTransforms);
    };

    // Start the scroll burst strip function
    const startScrollBurst = () => {
      if (scrollBurstRef.current !== null) return; // Already running
      
      log('Triggered scroll burst strip');
      const burstStart = Date.now();
      const burstDuration = 800; // 800ms burst on first scroll

      const burstStrip = () => {
        const elapsed = Date.now() - burstStart;
        if (elapsed > burstDuration) {
          log('Scroll burst strip complete');
          scrollBurstRef.current = null;
          return;
        }

        const nodes = document.querySelectorAll(
          '[data-framer-component-type], [data-projection-id], [style*="transform"]'
        );

        nodes.forEach((el) => {
          const htmlEl = el as HTMLElement;
          if (htmlEl.style.transform || (htmlEl.style as any).webkitTransform) {
            htmlEl.style.transform = '';
            (htmlEl.style as any).webkitTransform = '';
          }
        });

        scrollBurstRef.current = requestAnimationFrame(burstStrip);
      };

      scrollBurstRef.current = requestAnimationFrame(burstStrip);
    };

    // Scroll listener for first-scroll burst
    const handleScroll = () => {
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
        startScrollBurst();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    stripLoopRef.current = requestAnimationFrame(stripTransforms);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (stripLoopRef.current !== null) {
        cancelAnimationFrame(stripLoopRef.current);
        stripLoopRef.current = null;
        log('Transform strip loop cancelled (cleanup)');
      }
      if (scrollBurstRef.current !== null) {
        cancelAnimationFrame(scrollBurstRef.current);
        scrollBurstRef.current = null;
        log('Scroll burst strip cancelled (cleanup)');
      }
    };
  }, [location.pathname]);

  // iOS Safari zoom prevention - only on /products page
  useEffect(() => {
    // Only apply on /products route (not /products/:id)
    const isProductsListPage = location.pathname === '/products';
    if (!isProductsListPage) return;

    // Detect iOS Safari
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (!isIOSDevice || !isTouchDevice) return;

    // Debug mode - enable with ?iosZoomDebug=1
    const debugMode = new URLSearchParams(window.location.search).has('iosZoomDebug');
    const log = debugMode ? console.warn.bind(console, '[iOS Zoom Blocker]') : () => {};
    
    log('Activating iOS zoom prevention on /products');

    // Track last tap time for double-tap detection
    let lastTapTime = 0;

    // Handler for iOS gesture events (Safari-specific)
    const handleGestureEvent = (e: Event) => {
      e.preventDefault();
      log('Blocked gesture event:', e.type);
    };

    // Handler for multi-touch start (block pinch zoom at start)
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
        log('Blocked multi-touch start');
      }
    };

    // Handler for multi-touch (pinch zoom)
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
        log('Blocked multi-touch move');
      }
    };

    // NUCLEAR: Block any touchmove where scale !== 1 (Safari compositor zoom)
    const handleScaleTouchMove = (e: TouchEvent) => {
      // @ts-ignore - scale is Safari-specific property on TouchEvent
      if (e.scale !== undefined && e.scale !== 1) {
        e.preventDefault();
        log('Blocked scale touchmove, scale:', (e as any).scale);
      }
    };

    // Handler for double-tap zoom
    const handleTouchEnd = (e: TouchEvent) => {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapTime;
      
      // Detect double-tap (two taps within 300ms)
      if (timeSinceLastTap > 0 && timeSinceLastTap < 300) {
        e.preventDefault();
        log('Blocked double-tap zoom');
      }
      
      lastTapTime = now;
    };

    // Handler for ctrl+wheel zoom (Safari sometimes uses this)
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        log('Blocked ctrl+wheel zoom');
      }
    };

    // Add event listeners with { passive: false } - required for preventDefault to work on iOS
    document.addEventListener('gesturestart', handleGestureEvent, { passive: false });
    document.addEventListener('gesturechange', handleGestureEvent, { passive: false });
    document.addEventListener('gestureend', handleGestureEvent, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchmove', handleScaleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('wheel', handleWheel, { passive: false });

    log('Event listeners attached (including scale touchmove blocker)');

    // Cleanup on unmount or route change
    return () => {
      document.removeEventListener('gesturestart', handleGestureEvent);
      document.removeEventListener('gesturechange', handleGestureEvent);
      document.removeEventListener('gestureend', handleGestureEvent);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchmove', handleScaleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('wheel', handleWheel);
      log('Event listeners removed');
    };
  }, [location.pathname]);

  return null;
}
